import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Close from '@mui/icons-material/Close';
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Cookies } from 'react-cookie';
import moment from 'moment';
import ApplicationContext from '../ContextProvider';
import { Typography } from '@mui/material';

const PasswordReset = (): JSX.Element => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [password, setPassword] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [popover, setPopover] = useState<string>('');
    const navigate = useNavigate();
    const cookies: Cookies = new Cookies();
    const context = useContext(ApplicationContext);

    const updatePopover = (popoverContent: string): void => {
        // Reset our popover after 5 seconds if it isn't manually closed
        setTimeout(() => {
            setPopover('');
        }, 5000);
        setPopover(popoverContent);
    };

    const login = async (form: React.FormEvent): Promise<void> => {
        form.preventDefault();
        context.logUserEvent({ event_type: 'user_login_attempted', supplied_account: email });

        const loginResult: Response = await fetch('/users/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
            headers: {
                'Content-Type': 'application/json'
            }
        });
        const responseJson: any = await loginResult.json();
        if (loginResult.ok) {
            updatePopover('Welcome!');
            // Reset our controlled inputs to clear the form
            setPassword('');
            setEmail('');
            context.logUserEvent({
                event_type: 'user_login_successful',
                authenticated_permission_level: responseJson.data.permission_level
            });
            const expiryDate: Date = moment().add(7, 'days').toDate();
            cookies.set('cpebl', responseJson.data.cookie, { path: '/', expires: expiryDate, sameSite: 'strict' });
            context.setPermissionLevel(responseJson.data.permission_level);
            context.setVerifiedLogin(true);
            setTimeout(() => {
                navigate('/');
            }, 1000);
        } else {
            context.logUserEvent({ event_type: 'user_login_failed', event_message: responseJson.message });
            updatePopover(responseJson.message);
        }
    };

    return (
        <>
            <form
                onSubmit={(formData) => {
                    void login(formData);
                }}
            >
                <Grid container alignItems="center" justifyContent="center" direction="column">
                    <Grid item>
                        <h2>Login to your account</h2>
                        <FormControl sx={{ m: 1, width: { m: '50ch', sm: '30ch' } }} variant="outlined">
                            <TextField
                                id="email"
                                type={'text'}
                                required
                                helperText={'Example: John@gmail.com'}
                                value={email}
                                onChange={(emailElem) => {
                                    setEmail(emailElem.target.value);
                                }}
                                variant={'outlined'}
                                label="Email"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <FormControl sx={{ m: 1, width: { m: '50ch', sm: '30ch' } }} variant="outlined">
                            <TextField
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(password) => {
                                    setPassword(password.target.value);
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => {
                                                    setShowPassword(!showPassword);
                                                }}
                                                onMouseDown={() => {
                                                    setShowPassword(!showPassword);
                                                }}
                                                edge="end"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                label="Password"
                            />
                        </FormControl>
                    </Grid>
                    <Grid item>
                        <Button id="login_button" variant={'outlined'} type={'submit'}>
                            Login
                        </Button>
                    </Grid>
                    <Grid item>
                        <Typography>
                            Forgot your password? <br /> Click <Link to={'/forgot_password'}>Forgot Password</Link> to
                            reset your password
                        </Typography>
                    </Grid>
                </Grid>
            </form>
            <Popover
                id="informational_modal"
                disableAutoFocus
                open={popover.length > 1}
                anchorEl={document.getElementById('login_button')}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center'
                }}
                style={{ marginTop: '40px' }}
            >
                <Paper style={{ padding: '10px' }}>
                    {popover}
                    <IconButton
                        aria-label="close popover"
                        onClick={() => {
                            setPopover('');
                        }}
                        onMouseDown={() => {
                            setPopover('');
                        }}
                        edge="end"
                        style={{
                            display: popover.length > 1 ? 'inline-flex' : 'none',
                            marginLeft: '5px',
                            marginRight: '5px'
                        }}
                    >
                        <Close />
                    </IconButton>
                </Paper>
            </Popover>
        </>
    );
};

export default PasswordReset;
